import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Phone, ClipboardList, CheckCircle, AlertTriangle } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

export const EmergencyRepairsPage = () => {
  useEffect(() => {
    // SEO: Set page title and meta description
    document.title = "Emergency Roof Repairs Sydney | 24/7 Urgent Roofing Service | Dezire Metal Roofing";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Need emergency roof repairs in Sydney? Dezire Metal Roofing provides 24/7 urgent roofing support for leaks, storm damage, and urgent roofing issues across Greater Sydney.');
    }
    window.scrollTo(0, 0);
  }, []);

  const emergencyServices = [
    "Sudden leaks",
    "Storm-related roof damage",
    "Dislodged or damaged roofing sheets",
    "Urgent guttering issues affecting drainage",
    "Temporary and follow-up repair work"
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-red-900 via-gray-900 to-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <div className="inline-flex items-center bg-red-500/20 border border-red-500/50 rounded-full px-4 py-2 mb-6">
              <AlertTriangle className="w-5 h-5 text-red-400 mr-2" />
              <span className="text-red-300 font-semibold">24/7 Emergency Service Available</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="page-title">
              Emergency Roof Repairs <span className="text-primary">Sydney</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              When sudden roof damage puts your home at risk, fast action matters. Dezire Metal Roofing provides emergency roofing support across Greater Sydney for leaks, storm damage, and urgent roofing issues that need prompt attention.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="/call">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white" data-testid="emergency-call-btn">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now: 0433 973 844
                </Button>
              </a>
              <Link to="/book-inspection">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-gray-900">
                  <ClipboardList className="w-5 h-5 mr-2" />
                  Book Inspection
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                We Can Help With
              </h2>
              <ul className="space-y-4 mb-8">
                {emergencyServices.map((service, index) => (
                  <li key={`emergency-service-${index}-${service.slice(0, 10)}`} className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-primary mr-3 flex-shrink-0" />
                    <span className="text-gray-700 text-lg">{service}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-gray-100 p-6 rounded-xl">
                <h3 className="font-bold text-gray-900 mb-3">Fast Response When You Need It</h3>
                <p className="text-gray-700">
                  We understand that roof emergencies can happen at any time. Our team is ready to respond quickly to minimize damage and protect your home.
                </p>
              </div>
            </div>

            {/* Right Column - Emergency Contact */}
            <div>
              <div className="bg-red-50 border-2 border-red-200 p-8 rounded-xl">
                <div className="text-center">
                  <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Need Urgent Help?
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Call Dezire Metal Roofing now if you need urgent roofing assistance in Greater Sydney.
                  </p>

                  <a href="/call">
                    <Button size="lg" className="w-full bg-red-600 hover:bg-red-700 text-white text-xl py-6 mb-4" data-testid="content-call-btn">
                      <Phone className="w-6 h-6 mr-2" />
                      Call 0433 973 844
                    </Button>
                  </a>
                  
                  <p className="text-gray-500 text-sm">
                    Available 24/7 for emergencies
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Our Other Services
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/services/roof-repairs" className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <h3 className="font-bold text-gray-900 mb-2">Roof Repairs</h3>
              <p className="text-gray-600 text-sm">Non-urgent repairs</p>
            </Link>
            <Link to="/services/roof-replacement" className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <h3 className="font-bold text-gray-900 mb-2">Roof Replacement</h3>
              <p className="text-gray-600 text-sm">Full roof replacement</p>
            </Link>
            <Link to="/services/gutter-installation" className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <h3 className="font-bold text-gray-900 mb-2">Gutter Installation</h3>
              <p className="text-gray-600 text-sm">Gutters and drainage</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EmergencyRepairsPage;
